// src/departamentos/DepartamentosAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  IconButton,
  CircularProgress,
  Autocomplete,
  Avatar,
  MenuItem,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

export const DepartamentosAdmin = () => {
  const toast = useToast();

  // =========================
  // State: Data
  // =========================
  const [departamentos, setDepartamentos] = useState([]);
  const [usuariosSistema, setUsuariosSistema] = useState([]);
  const [usuariosPorDepartamento, setUsuariosPorDepartamento] = useState({}); // { [idDepto]: usuarios[] }

  // =========================
  // State: Loading
  // =========================
  const [loadingDeptos, setLoadingDeptos] = useState(true);
  const [loadingUsuariosSistema, setLoadingUsuariosSistema] = useState(false);
  const [loadingUsuariosDepto, setLoadingUsuariosDepto] = useState(false);

  const [saving, setSaving] = useState(false);

  // =========================
  // State: UI / Modals
  // =========================
  // Modal: usuarios del departamento
  const [modalUsuariosOpen, setModalUsuariosOpen] = useState(false);
  const [deptoUsuariosSel, setDeptoUsuariosSel] = useState(null);

  // Modal: editar departamento
  const [modalEditarOpen, setModalEditarOpen] = useState(false);
  const [deptoEditando, setDeptoEditando] = useState(null);
  const [editNombre, setEditNombre] = useState("");
  const [editDescripcion, setEditDescripcion] = useState("");

  // Modal: añadir usuario
  const [modalAddUserOpen, setModalAddUserOpen] = useState(false);
  const [deptoParaUsuario, setDeptoParaUsuario] = useState(null);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null); // {id,label,raw}
  const [rolUsuarioDepto, setRolUsuarioDepto] = useState("miembro");

  // Modal: eliminar definitivo (2 pasos)
  const [modalEliminarOpen, setModalEliminarOpen] = useState(false);
  const [deptoEliminar, setDeptoEliminar] = useState(null);
  const [impactoEliminar, setImpactoEliminar] = useState(null);
  const [confirmTextEliminar, setConfirmTextEliminar] = useState("");

  // =========================
  // Helpers: Headers
  // =========================
  const authOnly = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const authHeadersJson = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  // =========================
  // Helpers: Avatar
  // =========================
  const getAvatarUrl = (usuario) => {
    if (!usuario?.avatar_url) return "";
    return `${Global.url}${usuario.avatar_url}`;
  };

  const getIniciales = (usuario) => {
    const nombre = (usuario?.nombre || "").trim();
    const apellidos = (usuario?.apellidos || "").trim();
    const n1 = nombre ? nombre.split(" ")[0]?.[0] || "" : "";
    const a1 = apellidos ? apellidos.split(" ")[0]?.[0] || "" : "";
    const combo = (n1 + a1).toUpperCase();
    if (combo) return combo;
    if (nombre) return nombre[0].toUpperCase();
    return "?";
  };

  // =========================
  // Carga inicial
  // =========================
  useEffect(() => {
    cargarDepartamentos();
    cargarUsuariosSistema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDepartamentos = async () => {
    setLoadingDeptos(true);
    try {
      const res = await fetch(`${Global.url}departamentos?incluirInactivos=true`, {
        headers: authOnly(),
      });

      if (!res.ok) {
        toast.error?.("No se pudieron cargar los departamentos.");
        setDepartamentos([]);
        return;
      }

      const data = await res.json().catch(() => []);
      setDepartamentos(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al obtener departamentos:", e);
      toast.error?.("Error de conexión al cargar departamentos.");
      setDepartamentos([]);
    } finally {
      setLoadingDeptos(false);
    }
  };

  const cargarUsuariosSistema = async () => {
    setLoadingUsuariosSistema(true);
    try {
      const res = await fetch(`${Global.url}usuarios/todos`, { headers: authOnly() });
      const data = await res.json().catch(() => []);
      const lista = Array.isArray(data) ? data.filter((u) => u.activo !== false) : [];
      setUsuariosSistema(lista);
    } catch (e) {
      console.error("Error al obtener usuarios del sistema:", e);
      setUsuariosSistema([]);
    } finally {
      setLoadingUsuariosSistema(false);
    }
  };

  const cargarUsuariosDepartamento = async (departamentoId) => {
    if (!departamentoId) return;
    setLoadingUsuariosDepto(true);
    try {
      const res = await fetch(`${Global.url}departamentos/${departamentoId}/usuarios`, {
        headers: authOnly(),
      });

      if (!res.ok) {
        toast.error?.("No se pudieron cargar los usuarios del departamento.");
        setUsuariosPorDepartamento((prev) => ({ ...prev, [departamentoId]: [] }));
        return;
      }

      const data = await res.json().catch(() => []);
      setUsuariosPorDepartamento((prev) => ({
        ...prev,
        [departamentoId]: Array.isArray(data) ? data : [],
      }));
    } catch (e) {
      console.error("Error al obtener usuarios del departamento:", e);
      toast.error?.("Error de conexión al cargar usuarios del departamento.");
      setUsuariosPorDepartamento((prev) => ({ ...prev, [departamentoId]: [] }));
    } finally {
      setLoadingUsuariosDepto(false);
    }
  };

  // =========================
  // Acciones: usuarios (modal)
  // =========================
  const abrirModalUsuarios = async (departamento) => {
    setDeptoUsuariosSel(departamento);
    setModalUsuariosOpen(true);

    const id = departamento?.id;
    if (id && !usuariosPorDepartamento[id]) {
      await cargarUsuariosDepartamento(id);
    }
  };

  const cerrarModalUsuarios = () => {
    setModalUsuariosOpen(false);
    setDeptoUsuariosSel(null);
  };

  const quitarUsuarioDeDepartamento = async (departamentoId, usuarioId) => {
    if (!window.confirm("¿Quitar este usuario del departamento?")) return;

    try {
      const res = await fetch(`${Global.url}departamentos/${departamentoId}/usuarios/${usuarioId}`, {
        method: "DELETE",
        headers: authOnly(),
      });

      if (!res.ok) {
        toast.error?.("Error al quitar usuario del departamento.");
        return;
      }

      toast.success?.("Usuario quitado del departamento.");
      await cargarUsuariosDepartamento(departamentoId);
    } catch (e) {
      console.error(e);
      toast.error?.("Error de conexión al quitar usuario.");
    }
  };

  // =========================
  // Acciones: editar (modal)
  // =========================
  const abrirModalEditar = (departamento) => {
    setDeptoEditando(departamento);
    setEditNombre(departamento?.nombre || "");
    setEditDescripcion(departamento?.descripcion || "");
    setModalEditarOpen(true);
  };

  const cerrarModalEditar = () => {
    if (saving) return;
    setModalEditarOpen(false);
    setDeptoEditando(null);
    setEditNombre("");
    setEditDescripcion("");
  };

  const guardarCambiosDepto = async () => {
    if (!deptoEditando?.id) return;
    if (saving) return;

    if (!editNombre.trim()) {
      toast.warning?.("El nombre es obligatorio.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}departamentos/${deptoEditando.id}`, {
        method: "PUT",
        headers: authHeadersJson(),
        body: JSON.stringify({
          nombre: editNombre.trim(),
          descripcion: (editDescripcion || "").trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error?.(data?.message || "No se pudo guardar el departamento.");
        return;
      }

      toast.success?.("Departamento actualizado.");
      cerrarModalEditar();
      await cargarDepartamentos();
    } catch (e) {
      console.error("Error al guardar departamento:", e);
      toast.error?.("Error de conexión al guardar.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // Acciones: añadir usuario (modal)
  // =========================
  const abrirModalAgregarUsuario = async (departamento) => {
    setDeptoParaUsuario(departamento);
    setUsuarioSeleccionado(null);
    setRolUsuarioDepto("miembro");
    setModalAddUserOpen(true);

    // Asegurar cache para filtrar disponibles
    const id = departamento?.id;
    if (id && !usuariosPorDepartamento[id]) {
      await cargarUsuariosDepartamento(id);
    }
  };

  const cerrarModalAgregarUsuario = () => {
    if (saving) return;
    setModalAddUserOpen(false);
    setDeptoParaUsuario(null);
    setUsuarioSeleccionado(null);
    setRolUsuarioDepto("miembro");
  };

  const usuariosDisponibles = useMemo(() => {
    const deptId = deptoParaUsuario?.id;
    if (!deptId) return [];

    const yaAsignados = new Set((usuariosPorDepartamento[deptId] || []).map((u) => Number(u.id)));

    return (usuariosSistema || [])
      .filter((u) => !yaAsignados.has(Number(u.id)))
      .slice()
      .sort((a, b) =>
        `${a.nombre || ""} ${a.apellidos || ""}`.localeCompare(`${b.nombre || ""} ${b.apellidos || ""}`)
      );
  }, [deptoParaUsuario, usuariosPorDepartamento, usuariosSistema]);

  const usuarioOptions = useMemo(() => {
    return usuariosDisponibles.map((u) => ({
      id: Number(u.id),
      label: `${u.nombre || ""} ${u.apellidos || ""} (${u.email || u.dni || "s/d"})`.trim(),
      raw: u,
    }));
  }, [usuariosDisponibles]);

  const agregarUsuarioADepartamento = async () => {
    if (!deptoParaUsuario?.id || !usuarioSeleccionado?.id) {
      toast.warning?.("Selecciona un usuario antes de guardar.");
      return;
    }
    if (saving) return;

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}departamentos/${deptoParaUsuario.id}/usuarios`, {
        method: "POST",
        headers: authHeadersJson(),
        body: JSON.stringify({
          usuarios: [{ usuario_id: Number(usuarioSeleccionado.id), rol: rolUsuarioDepto }],
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error?.(data?.message || "No se pudo añadir el usuario.");
        return;
      }

      toast.success?.("Usuario añadido al departamento.");
      await cargarUsuariosDepartamento(deptoParaUsuario.id);
      cerrarModalAgregarUsuario();
    } catch (e) {
      console.error(e);
      toast.error?.("Error de conexión al añadir usuario.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // Eliminar definitivo (2 pasos)
  // =========================
  const solicitarEliminarDepartamento = async (departamentoId) => {
    setDeptoEliminar(null);
    setImpactoEliminar(null);
    setConfirmTextEliminar("");
    setModalEliminarOpen(true);

    try {
      const res = await fetch(`${Global.url}departamentos/${departamentoId}`, {
        method: "DELETE",
        headers: authOnly(),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error?.(data?.message || "Error al preparar la eliminación.");
        return;
      }

      const departamento =
        data?.departamento ||
        departamentos.find((d) => Number(d.id) === Number(departamentoId)) ||
        null;

      setDeptoEliminar(departamento);
      setImpactoEliminar(data?.impacto || null);
    } catch (e) {
      console.error(e);
      toast.error?.("Error de conexión al preparar la eliminación.");
    }
  };

  const cerrarModalEliminar = () => {
    if (saving) return;
    setModalEliminarOpen(false);
    setDeptoEliminar(null);
    setImpactoEliminar(null);
    setConfirmTextEliminar("");
  };

  const confirmarEliminarDepartamento = async () => {
    if (!deptoEliminar?.id) return;
    if (saving) return;

    if (confirmTextEliminar.trim().toUpperCase() !== "ELIMINAR") {
      toast.warning?.('Para confirmar, escribe exactamente: "ELIMINAR"');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}departamentos/${deptoEliminar.id}?confirm=ELIMINAR`, {
        method: "DELETE",
        headers: authOnly(),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error?.(data?.message || "No se pudo eliminar el departamento.");
        return;
      }

      toast.success?.("Departamento eliminado definitivamente.");
      cerrarModalEliminar();
      cerrarModalUsuarios(); // por si estaba abierto ese depto

      await cargarDepartamentos();
    } catch (e) {
      console.error(e);
      toast.error?.("Error de conexión al eliminar el departamento.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // DataGrid rows + columns
  // =========================
  const rows = useMemo(() => {
    return (departamentos || [])
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map((d) => ({
        ...d,
        id: d.id,
        descripcion_txt: d.descripcion || "-",
      }));
  }, [departamentos]);

  const columns = useMemo(
    () => [
      { field: "nombre", headerName: "Departamento", flex: 0.7, minWidth: 170 },
      { field: "descripcion_txt", headerName: "Descripción", flex: 1.6, minWidth: 340 },
      {
        field: "acciones",
        headerName: "Acciones",
        sortable: false,
        filterable: false,
        width: 520,
        minWidth: 520,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%", width: "100%" }}>
            <Button
              size="small"
              startIcon={<GroupIcon />}
              onClick={() => abrirModalUsuarios(params.row)}
              disabled={modalUsuariosOpen || modalEditarOpen || modalAddUserOpen || modalEliminarOpen}
            >
              Usuarios
            </Button>

            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => abrirModalEditar(params.row)}
              disabled={modalUsuariosOpen || modalEditarOpen || modalAddUserOpen || modalEliminarOpen}
            >
              Editar
            </Button>

            <Button
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={() => abrirModalAgregarUsuario(params.row)}
              disabled={modalUsuariosOpen || modalEditarOpen || modalAddUserOpen || modalEliminarOpen}
            >
              Añadir
            </Button>

            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => solicitarEliminarDepartamento(params.row.id)}
              disabled={saving || modalUsuariosOpen || modalEditarOpen || modalAddUserOpen}
            >
              Eliminar
            </Button>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saving, modalUsuariosOpen, modalEditarOpen, modalAddUserOpen, modalEliminarOpen]
  );

  // =========================
  // DataGrid: usuarios del departamento (modal)
  // =========================
  const rowsUsuariosDepto = useMemo(() => {
    const deptId = deptoUsuariosSel?.id;
    const lista = deptId ? usuariosPorDepartamento[deptId] || [] : [];

    return (lista || []).map((u) => ({
      id: u.id,
      nombre_txt: `${u.nombre || ""} ${u.apellidos || ""}`.trim(),
      email_txt: u.email || "-",
      rol_txt: u.rol || "miembro",
      miembro_activo: u.miembro_activo !== false,
      raw: u,
    }));
  }, [deptoUsuariosSel, usuariosPorDepartamento]);

  const columnsUsuariosDepto = useMemo(
    () => [
      {
        field: "avatar",
        headerName: "",
        width: 64,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const u = params.row?.raw;
          const src = getAvatarUrl(u);
          return (
            <Avatar src={src || undefined} sx={{ width: 34, height: 34 }}>
              {!src ? getIniciales(u) : null}
            </Avatar>
          );
        },
      },
      { field: "nombre_txt", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "email_txt", headerName: "Email", flex: 1, minWidth: 220 },
      { field: "rol_txt", headerName: "Rol", width: 140 },
      {
        field: "acciones",
        headerName: "Acciones",
        width: 160,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const deptId = deptoUsuariosSel?.id;
          const u = params.row?.raw;

          if (params.row?.miembro_activo === false) {
            return (
              <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                Inactivo
              </Typography>
            );
          }

          return (
            <Button
              size="small"
              onClick={() => quitarUsuarioDeDepartamento(deptId, u.id)}
              disabled={saving}
            >
              Quitar
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saving, deptoUsuariosSel?.id]
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Gestión de departamentos
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loadingDeptos}
            pagination
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loadingDeptos ? "Cargando…" : "No hay departamentos registrados."}
                </Box>
              ),
            }}
          />
        </Paper>

        {/* =========================
            Dialog: Usuarios del departamento
           ========================= */}
        <Dialog open={modalUsuariosOpen} onClose={cerrarModalUsuarios} fullWidth maxWidth="md">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            Usuarios de “{deptoUsuariosSel?.nombre || "—"}”
            <IconButton aria-label="Cerrar" onClick={cerrarModalUsuarios} disabled={saving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Paper sx={{ p: 1 }}>
              <DataGrid
                autoHeight
                rows={rowsUsuariosDepto}
                columns={columnsUsuariosDepto}
                loading={loadingUsuariosDepto}
                disableRowSelectionOnClick
                pagination
                initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
                pageSizeOptions={[10, 25, 50]}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ p: 2, color: "text.secondary" }}>
                      {loadingUsuariosDepto ? "Cargando…" : "No hay usuarios en este departamento."}
                    </Box>
                  ),
                }}
              />
            </Paper>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cerrarModalUsuarios} disabled={saving}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Editar departamento
           ========================= */}
        <Dialog open={modalEditarOpen} onClose={cerrarModalEditar} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            Editar departamento
            <IconButton aria-label="Cerrar" onClick={cerrarModalEditar} disabled={saving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Nombre"
                value={editNombre}
                onChange={(e) => setEditNombre(e.target.value)}
                required
                disabled={saving}
              />

              <TextField
                label="Descripción"
                value={editDescripcion}
                onChange={(e) => setEditDescripcion(e.target.value)}
                placeholder="Opcional"
                multiline
                minRows={3}
                disabled={saving}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cerrarModalEditar} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={guardarCambiosDepto} disabled={saving} startIcon={<SaveIcon />}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Añadir usuario a departamento
           ========================= */}
        <Dialog open={modalAddUserOpen} onClose={cerrarModalAgregarUsuario} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            Añadir usuario a “{deptoParaUsuario?.nombre || "—"}”
            <IconButton aria-label="Cerrar" onClick={cerrarModalAgregarUsuario} disabled={saving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Autocomplete
                options={usuarioOptions}
                value={usuarioSeleccionado}
                onChange={(_, v) => setUsuarioSeleccionado(v)}
                loading={loadingUsuariosSistema}
                isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                getOptionLabel={(o) => o?.label || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuario"
                    placeholder="Buscar usuario…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsuariosSistema ? <CircularProgress size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                clearOnEscape
                disabled={saving}
              />

              <TextField
                select
                label="Rol"
                value={rolUsuarioDepto}
                onChange={(e) => setRolUsuarioDepto(e.target.value)}
                disabled={saving}
              >
                <MenuItem value="miembro">Miembro</MenuItem>
                <MenuItem value="responsable">Responsable</MenuItem>
                <MenuItem value="backup">Backup</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cerrarModalAgregarUsuario} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={agregarUsuarioADepartamento} disabled={saving || !usuarioSeleccionado}>
              {saving ? "Guardando..." : "Añadir"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* =========================
            Dialog: Eliminar definitivamente (2 pasos)
           ========================= */}
        <Dialog open={modalEliminarOpen} onClose={cerrarModalEliminar} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            Eliminar departamento definitivamente
            <IconButton aria-label="Cerrar" onClick={cerrarModalEliminar} disabled={saving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2">
                Vas a eliminar el departamento <b>“{deptoEliminar?.nombre || "—"}”</b> y se borrará también la
                información dependiente (tareas, peticiones e históricos).
              </Typography>

              {impactoEliminar ? (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Impacto estimado
                  </Typography>
                  <Typography variant="body2">• Tareas: {impactoEliminar.tareas}</Typography>
                  <Typography variant="body2">• Peticiones: {impactoEliminar.peticiones}</Typography>
                  <Typography variant="body2">• Histórico tareas: {impactoEliminar.tareas_historico}</Typography>
                  <Typography variant="body2">• Histórico peticiones: {impactoEliminar.peticiones_historico}</Typography>
                </Paper>
              ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {deptoEliminar ? "Sin detalle de impacto." : "Preparando previsualización..."}
                </Typography>
              )}

              <TextField
                label='Escribe "ELIMINAR" para confirmar'
                value={confirmTextEliminar}
                onChange={(e) => setConfirmTextEliminar(e.target.value)}
                placeholder="ELIMINAR"
                disabled={saving}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={cerrarModalEliminar} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={confirmarEliminarDepartamento} disabled={saving}>
              {saving ? "Eliminando..." : "Eliminar definitivamente"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default DepartamentosAdmin;
